/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography, Avatar, Chip, Skeleton, Tooltip } from '@mui/material';
import Iconify from '../Iconify';
import { availPractitionerName, parseDentallyDate } from '../../utils/dentallyUtils';
import { emptyStateBoxStyle } from '../../styles/DentallyBookingStyle';
import { zonesForDay, dayKeyOf, formatZoneWindow, zoneForSlot } from '../../utils/dentallyZones';

const parseSlotDate = (str) => parseDentallyDate(str) || (str ? new Date(str) : null);

const getSlotPractitionerId = (slot) => {
    if (slot._queried_practitioner_id != null) return slot._queried_practitioner_id;
    if (slot.practitioner_id != null) return slot.practitioner_id;
    if (typeof slot.practitioner === 'object' && slot.practitioner?.id != null) return slot.practitioner.id;
    if (slot.practitioner != null) return slot.practitioner;
    return null;
};

const formatTime = (isoString) => {
    try {
        const d = parseSlotDate(isoString);
        if (!d || Number.isNaN(d.getTime())) return '--:--';
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return '--:--'; }
};

const formatDuration = (startIso, finishIso) => {
    try {
        const s = parseSlotDate(startIso);
        const f = parseSlotDate(finishIso);
        if (!s || !f || Number.isNaN(s.getTime()) || Number.isNaN(f.getTime())) return null;
        const mins = Math.round((f - s) / 60000);
        if (mins <= 0) return null;
        if (mins >= 60 && mins % 60 === 0) return `${mins / 60}h`;
        if (mins > 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
        return `${mins}m`;
    } catch { return null; }
};

const formatDate = (isoString) => {
    try {
        const d = parseSlotDate(isoString);
        if (!d || Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch { return ''; }
};

const getRelativeLabel = (isoString) => {
    try {
        const slotDate = parseSlotDate(isoString);
        if (!slotDate || Number.isNaN(slotDate.getTime())) return null;
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const isSameDay = (a, b) =>
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate();
        if (isSameDay(slotDate, today)) return 'Today';
        if (isSameDay(slotDate, tomorrow)) return 'Tomorrow';
        return null;
    } catch { return null; }
};

// ── Time period helpers ───────────────────────────────────────────────────────
const TIME_PERIODS = [
    { key: 'morning', label: 'Morning', icon: 'mdi:weather-sunny', color: '#FFAB00', minHour: 0, maxHour: 12 },
    { key: 'afternoon', label: 'Afternoon', icon: 'mdi:white-balance-sunny', color: '#FF6C40', minHour: 12, maxHour: 17 },
    { key: 'evening', label: 'Evening', icon: 'mdi:weather-night', color: '#5C6BC0', minHour: 17, maxHour: 24 },
];

const getTimePeriod = (isoString) => {
    try {
        const d = parseSlotDate(isoString);
        if (!d || Number.isNaN(d.getTime())) return 'morning';
        const h = d.getHours();
        if (h < 12) return 'morning';
        if (h < 17) return 'afternoon';
        return 'evening';
    } catch { return 'morning'; }
};

const LoadingSkeleton = () => (
    <Box>
        {[0, 1].map((group) => (
            <Box key={group} sx={{ mb: 2 }}>
                <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
                    <Skeleton variant='text' width={80} height={14} />
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                    <Skeleton variant='text' width={40} height={12} />
                </Stack>
                {[0, 1].map((row) => (
                    <Stack key={row} direction='row' alignItems='center' spacing={1} sx={{ mb: 0.75 }}>
                        <Skeleton variant='circular' width={20} height={20} sx={{ flexShrink: 0 }} />
                        <Skeleton variant='text' width={80} height={13} sx={{ flexShrink: 0 }} />
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                            {[0, 1, 2, 3].map((i) => (
                                <Skeleton key={i} variant='rounded' width={46} height={42} sx={{ borderRadius: '10px' }} />
                            ))}
                        </Box>
                    </Stack>
                ))}
            </Box>
        ))}
    </Box>
);

// Stamp the covering zone onto the slot as it is chosen.
//
// The Confirm step needs to warn when a time sits inside a reserved block, and
// this component is the only place that already knows. Carrying it on the slot
// avoids both a second provider call and threading zones through the wizard.
// Underscore-prefixed to match the existing `_queried_practitioner_name` /
// `_manual` convention for client-side annotations that never reach the API.
const withZone = (slot, zones) => (
  slot && !slot._manual ? { ...slot, _zone: zoneForSlot(zones, slot) } : slot
);

const DentallyAvailabilityResults = ({ results, onSelect, practitioners, selectedSlot, loading, compact, zones }) => {
    const practitionerMap = useMemo(() => {
        const map = {};
        (practitioners || []).forEach((p) => {
            map[p.id] = p;
            map[String(p.id)] = p;
        });
        return map;
    }, [practitioners]);

    // Group by date → time period → practitioner
    const grouped = useMemo(() => {
        if (!results || results.length === 0) return [];
        const byDate = {};
        const dateOrder = [];

        results.forEach((slot) => {
            const dateKey = formatDate(slot.start_time);
            if (!byDate[dateKey]) {
                byDate[dateKey] = { periods: {}, firstSlotTime: slot.start_time, total: 0 };
                dateOrder.push(dateKey);
            }
            const period = getTimePeriod(slot.start_time);
            if (!byDate[dateKey].periods[period]) {
                byDate[dateKey].periods[period] = { byPract: {}, practOrder: [], total: 0 };
            }
            const periodData = byDate[dateKey].periods[period];
            const practId = String(getSlotPractitionerId(slot) ?? 'unknown');
            if (!periodData.byPract[practId]) {
                periodData.byPract[practId] = [];
                periodData.practOrder.push(practId);
            }
            periodData.byPract[practId].push(slot);
            periodData.total += 1;
            byDate[dateKey].total += 1;
        });

        return dateOrder.map((dateKey) => ({
            dateKey,
            firstSlotTime: byDate[dateKey].firstSlotTime,
            total: byDate[dateKey].total,
            periods: TIME_PERIODS
                .filter((tp) => byDate[dateKey].periods[tp.key])
                .map((tp) => {
                    const pd = byDate[dateKey].periods[tp.key];
                    return {
                        ...tp,
                        total: pd.total,
                        practGroups: pd.practOrder.map((practId) => ({
                            practId,
                            slots: pd.byPract[practId],
                        })),
                    };
                }),
        }));
    }, [results]);

    const getPractitionerInfo = (practId, sampleSlot) => {
        const taggedName = sampleSlot?._queried_practitioner_name || null;
        if (practId !== 'unknown') {
            const pract = practitionerMap[practId] || practitionerMap[String(practId)];
            if (pract) {
                const u = pract.user || {};
                return {
                    name: availPractitionerName(pract),
                    initials: ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase() || '?',
                    colour: pract.colour || '#5C6BC0',
                };
            }
        }
        if (taggedName) {
            const parts = taggedName.split(' ');
            const init = (parts[0]?.[0] || '') + (parts[parts.length > 1 ? parts.length - 1 : 0]?.[0] || '');
            return { name: taggedName, initials: init.toUpperCase() || '?', colour: '#5C6BC0' };
        }
        return { name: 'Unknown', initials: '?', colour: '#90A4AE' };
    };

    // Skeleton ONLY while there is nothing to show. A search fans out into
    // several provider calls and publishes after each one, so slots arrive
    // progressively — but this used to discard them and keep rendering the
    // skeleton for the whole run, including the pause that the 4-lookups-a-
    // minute budget forces between batches. Reported from a live demo: the
    // network log showed the data had arrived while the UI still said loading.
    // Once anything is in hand it is shown, and the caller renders its own
    // progress line above.
    if (loading && (!results || results.length === 0)) return <LoadingSkeleton />;

    if (!results || results.length === 0) {
        return (
            <Box sx={emptyStateBoxStyle}>
                <Box sx={{
                    width: 44, height: 44, borderRadius: '50%', mx: 'auto', mb: 1.5,
                    bgcolor: 'rgba(145,158,171,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Iconify icon='mdi:calendar-blank-outline' width={22} sx={{ color: 'text.disabled' }} />
                </Box>
                <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    No availability found
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                    Try a wider timeframe or more practitioners
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {grouped.map(({ dateKey, periods, firstSlotTime, total }) => {
                const relLabel = getRelativeLabel(firstSlotTime);
                // Only the diaries on screen for this day may label it.
                const dayPractIds = periods.flatMap((p) => p.practGroups.map((g) => g.practId));
                const dayZones = zonesForDay(zones, dayKeyOf(firstSlotTime), dayPractIds);
                const isToday = relLabel === 'Today';

                return (
                    <Box key={dateKey} sx={{ mb: 2.5 }}>
                        {/* Date header with divider */}
                        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
                            <Typography sx={{
                                fontSize: 11, fontWeight: 700, letterSpacing: '0.2px',
                                color: isToday ? '#00A76F' : 'text.secondary',
                                whiteSpace: 'nowrap',
                            }}>
                                {relLabel ? `${relLabel} · ${dateKey}` : dateKey}
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                            <Typography sx={{ fontSize: 10, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                {total} slot{total !== 1 ? 's' : ''}
                            </Typography>
                        </Stack>

                        {/* What this day's time is reserved FOR.
                            Dentally's availability is the rota minus booked
                            appointments and nothing else, so a free period says
                            nothing about whether the practice keeps it for
                            exams, treatment or emergencies. Shown as a band
                            rather than only on hover: hover is invisible on
                            touch and easy to miss, and the whole point is that
                            nobody books into the wrong block without noticing.
                            Uses the provider's own colour so it reads the way
                            the practice already sees its diary. */}
                        {dayZones.length > 0 && (
                            <Stack
                                direction='row'
                                spacing={0.75}
                                sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}
                                data-testid='dentally-zone-band'
                            >
                                {dayZones.map((z) => (
                                    <Tooltip
                                        key={z.id || `${z.label}-${z.start_time}`}
                                        title={`${z.label || 'Reserved'} \u00b7 ${formatZoneWindow(z)} \u00b7 reserved in Dentally`}
                                        arrow
                                        placement='top'
                                    >
                                        <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                            px: 0.75, py: 0.25, borderRadius: '6px',
                                            border: '1px solid', borderColor: 'divider',
                                            maxWidth: '100%',
                                        }}>
                                            <Box sx={{
                                                width: 3, alignSelf: 'stretch', borderRadius: '2px',
                                                bgcolor: z.colour || 'text.disabled', flexShrink: 0,
                                            }} />
                                            <Typography noWrap sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary' }}>
                                                {z.label || 'Reserved'}
                                            </Typography>
                                            <Typography noWrap sx={{ fontSize: 10, color: 'text.disabled' }}>
                                                {formatZoneWindow(z)}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                ))}
                            </Stack>
                        )}

                        {/* Time periods */}
                        {periods.map(({ key: periodKey, label: periodLabel, icon, color, practGroups, total: periodTotal }) => (
                            <Box key={periodKey} sx={{ mb: 1.5 }}>
                                {/* Period header */}
                                <Stack direction='row' alignItems='center' spacing={0.75} sx={{ mb: 0.75, mt: 0.25, px: 0.5 }}>
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                        px: 1, py: 0.25, borderRadius: '6px',
                                        bgcolor: `${color}10`,
                                    }}>
                                        <Iconify icon={icon} width={13} sx={{ color }} />
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color, letterSpacing: '0.3px' }}>
                                            {periodLabel}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={periodTotal}
                                        size='small'
                                        sx={{
                                            fontSize: '10px', fontWeight: 600, height: 18, minWidth: 18,
                                            borderRadius: '4px', bgcolor: 'rgba(145,158,171,0.08)', color: 'text.disabled',
                                        }}
                                    />
                                </Stack>

                                {/* One row per practitioner */}
                                <Box sx={{ display: 'flex', flexDirection: compact ? 'row' : 'column', flexWrap: compact ? 'wrap' : 'nowrap', gap: compact ? 0.375 : 0.75 }}>
                                    {practGroups.map(({ practId, slots }) => {
                                        const pract = getPractitionerInfo(practId, slots[0]);

                                        return compact ? (
                                            /* Compact: flat pill list, no practitioner labels */
                                            <React.Fragment key={practId}>
                                                {slots.map((slot, idx) => {
                                                    const slotPractId = getSlotPractitionerId(slot);
                                                    const selectedPractId = selectedSlot ? getSlotPractitionerId(selectedSlot) : null;
                                                    const isSelected = selectedSlot && selectedSlot.start_time === slot.start_time && String(selectedPractId) === String(slotPractId);
                                                    return (
                                                        <Box key={`${slot.start_time}-${idx}`} onClick={() => onSelect(withZone(slot, zones))}
                                                            sx={{
                                                                px: 0.75, py: 0.25, borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                                                                cursor: 'pointer', border: '1px solid', fontVariantNumeric: 'tabular-nums',
                                                                borderColor: isSelected ? '#00AB55' : 'rgba(145,158,171,0.2)',
                                                                color: isSelected ? '#00AB55' : '#637381',
                                                                background: isSelected ? 'rgba(0,171,85,0.06)' : '#fff',
                                                                transition: 'all 150ms ease',
                                                                '&:hover': { borderColor: '#00AB55', color: '#00AB55', background: 'rgba(0,171,85,0.04)' },
                                                            }}>
                                                            {formatTime(slot.start_time)}
                                                        </Box>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ) : (
                                            <Stack key={practId} direction='row' alignItems='flex-start' spacing={1}>
                                                {/* Practitioner label — fixed width */}
                                                <Stack
                                                    direction='row'
                                                    alignItems='center'
                                                    spacing={0.75}
                                                    sx={{ width: 120, flexShrink: 0, pt: 0.5 }}
                                                >
                                                    <Avatar sx={{
                                                        width: 20, height: 20,
                                                        fontSize: 8, fontWeight: 700,
                                                        bgcolor: pract.colour,
                                                        flexShrink: 0,
                                                    }}>
                                                        {pract.initials}
                                                    </Avatar>
                                                    <Typography noWrap sx={{
                                                        fontSize: 11,
                                                        color: 'text.secondary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}>
                                                        {pract.name}
                                                    </Typography>
                                                </Stack>

                                                {/* Time pills */}
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flex: 1 }}>
                                                    {slots.map((slot, idx) => {
                                                        const slotPractId = getSlotPractitionerId(slot);
                                                        const selectedPractId = selectedSlot ? getSlotPractitionerId(selectedSlot) : null;
                                                        const isSelected =
                                                            selectedSlot &&
                                                            selectedSlot.start_time === slot.start_time &&
                                                            String(selectedPractId) === String(slotPractId);
                                                        const dur = formatDuration(slot.start_time, slot.finish_time);

                                                        return (
                                                            <Box
                                                                key={`${slot.start_time}-${idx}`}
                                                                onClick={() => onSelect(withZone(slot, zones))}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.75,
                                                                    cursor: 'pointer',
                                                                    px: 1.25,
                                                                    py: 0.75,
                                                                    borderRadius: '10px',
                                                                    border: '1.5px solid',
                                                                    borderColor: isSelected ? 'success.main' : 'rgba(145,158,171,0.2)',
                                                                    bgcolor: isSelected ? 'rgba(0,167,111,0.08)' : '#fff',
                                                                    boxShadow: isSelected
                                                                        ? '0 0 0 1px rgba(0,167,111,0.15)'
                                                                        : '0 1px 3px rgba(145,158,171,0.1)',
                                                                    transition: 'all 0.15s ease',
                                                                    '&:hover': {
                                                                        borderColor: isSelected ? 'success.dark' : 'rgba(0,167,111,0.4)',
                                                                        bgcolor: isSelected ? 'rgba(0,167,111,0.12)' : 'rgba(0,167,111,0.03)',
                                                                        boxShadow: '0 2px 8px rgba(145,158,171,0.16)',
                                                                        transform: 'translateY(-1px)',
                                                                    },
                                                                }}
                                                            >
                                                                {isSelected && (
                                                                    <Iconify
                                                                        icon='mdi:check-circle'
                                                                        width={14}
                                                                        sx={{ color: 'success.main', flexShrink: 0 }}
                                                                    />
                                                                )}
                                                                <Typography sx={{
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                    fontVariantNumeric: 'tabular-nums',
                                                                    color: isSelected ? 'success.main' : 'text.primary',
                                                                    lineHeight: 1.2,
                                                                    letterSpacing: '0.3px',
                                                                }}>
                                                                    {formatTime(slot.start_time)}
                                                                </Typography>
                                                                {dur && (
                                                                    <Typography sx={{
                                                                        fontSize: 10,
                                                                        fontWeight: 600,
                                                                        color: isSelected ? 'rgba(0,167,111,0.7)' : 'text.disabled',
                                                                        lineHeight: 1,
                                                                        px: 0.5,
                                                                        py: 0.125,
                                                                        borderRadius: '4px',
                                                                        bgcolor: isSelected
                                                                            ? 'rgba(0,167,111,0.08)'
                                                                            : 'rgba(145,158,171,0.08)',
                                                                    }}>
                                                                        {dur}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Stack>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                );
            })}
        </Box>
    );
};

DentallyAvailabilityResults.propTypes = {
    results: PropTypes.array,
    onSelect: PropTypes.func.isRequired,
    practitioners: PropTypes.array,
    selectedSlot: PropTypes.object,
    loading: PropTypes.bool,
    // Advisory Dentally zones for the searched window; never filters slots.
    zones: PropTypes.arrayOf(PropTypes.object),
    compact: PropTypes.bool,
};

DentallyAvailabilityResults.defaultProps = {
    results: [],
    practitioners: [],
    selectedSlot: null,
    loading: false,
    compact: false,
};

export default DentallyAvailabilityResults;
